// src/composables/useLanguage.js
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Language configuration
export const SUPPORTED_LANGUAGES = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "usa",
    rtl: false
  },
  {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    flag: "es",
    rtl: false
  },
  {
    code: "fr",
    name: "French",
    nativeName: "Français",
    flag: "fr",
    rtl: false
  },
  {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    flag: "de",
    rtl: false
  },
  {
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    flag: "ar",
    rtl: true
  },
  {
    code: "zh",
    name: "Chinese",
    nativeName: "中文",
    flag: "cn",
    rtl: false
  },
  {
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    flag: "jp",
    rtl: false
  },
  {
    code: "bn",
    name: "Bengali",
    nativeName: "বাংলা",
    flag: "bd",
    rtl: false
  },
  {
    code: "ko",
    name: "Korean",
    nativeName: "한국어",
    flag: "kr",
    rtl: false
  }
];

export const RTL_LANGUAGES = ["ar"];

export function useLanguage() {
  const i18n = useI18n();

  const currentLanguage = computed(() => {
    return (
      SUPPORTED_LANGUAGES.find((lang) => lang.code === i18n.locale.value) || SUPPORTED_LANGUAGES[0]
    );
  });

  const isRTL = computed(() => {
    return RTL_LANGUAGES.includes(i18n.locale.value);
  });

  function applyDirection(locale) {
    const htmlElement = document.documentElement;
    const isRTLLocale = RTL_LANGUAGES.includes(locale);

    if (isRTLLocale) {
      htmlElement.setAttribute("dir", "rtl");
      htmlElement.setAttribute("lang", locale);
      document.body.classList.add("rtl");
    } else {
      htmlElement.setAttribute("dir", "ltr");
      htmlElement.setAttribute("lang", locale);
      document.body.classList.remove("rtl");
    }
  }

  function setDayjsLocale(locale) {
    // Import dayjs dynamically to avoid bundle bloat
    import("dayjs").then(({ default: dayjs }) => {
      const localeMap = {
        en: "en",
        es: "es",
        fr: "fr",
        de: "de",
        ar: "ar",
        zh: "zh",
        ja: "ja",
        bn: "bn",
        ko: "ko"
      };
      dayjs.locale(localeMap[locale] || "en");
    });
  }

  function changeLocale(locale, shouldReload = true) {
    if (!SUPPORTED_LANGUAGES.find((lang) => lang.code === locale)) {
      console.warn(`Unsupported locale: ${locale}`);
      return;
    }

    i18n.locale.value = locale;
    localStorage.setItem("ptcwizard_locale", locale);

    // Apply RTL/LTR direction and dayjs locale
    applyDirection(locale);
    setDayjsLocale(locale);

    // Reload to ensure all components update properly
    if (shouldReload) {
      window.location.reload();
    }
  }

  function getLanguageByCode(code) {
    return SUPPORTED_LANGUAGES.find((lang) => lang.code === code);
  }

  function getAvailableLanguages() {
    return SUPPORTED_LANGUAGES;
  }

  return {
    currentLanguage,
    isRTL,
    supportedLanguages: SUPPORTED_LANGUAGES,
    rtlLanguages: RTL_LANGUAGES,
    applyDirection,
    setDayjsLocale,
    changeLocale,
    getLanguageByCode,
    getAvailableLanguages
  };
}

// Utility function to get language direction
export function getLanguageDirection(locale) {
  return RTL_LANGUAGES.includes(locale) ? "rtl" : "ltr";
}

// Utility function to check if locale is RTL
export function isRTLLocale(locale) {
  return RTL_LANGUAGES.includes(locale);
}
